/**
 * Samsung remote → W3C keys, and the guard that keeps a guest inside the app
 * (TIZEN-15).
 *
 * MEASURED ON A PANEL 2026-08-11, and the finding was worse than "back doesn't
 * work": pressing Exit took the screen away from Foyer entirely. A television
 * in a hotel room that a guest can quit is not a product - the next guest walks
 * into the Samsung home screen and Foyer is simply gone.
 *
 * Two separate problems hide behind that one symptom, and only one of them is
 * ours to fix:
 *
 *  1. THE APP NEVER HEARD THE KEY. Samsung's remote does not emit the `key`
 *     names the rest of this app is written against. Back arrives as
 *     `keyCode 10009` with a `key` of "XF86Back" (or nothing at all), so every
 *     `k === "Escape" || k === "Backspace"` branch in useFocusGrid, the
 *     launcher kit and PairingScreen silently failed to match. Back was not
 *     broken - it was never delivered. That is what this module fixes: one
 *     translation layer, in the capture phase, so the existing handlers keep
 *     working unchanged on all four platforms.
 *
 *  2. HOME AND SETTINGS BELONG TO THE TELEVISION. They are handled by the
 *     firmware before any application sees them, and no web app can intercept
 *     them - not this one, not Uniguest's. Locking those out is a HOTEL MODE
 *     setting on the panel, not code, and pretending otherwise here would just
 *     hide the requirement until a real hotel found it. See
 *     docs/tizen-first-light.md.
 *
 * Exit (10182) sits between the two: the firmware will close the app, unless
 * the app has claimed the key through `tizen.tvinputdevice.registerKey`. We
 * claim it and swallow it. Whether the claim is honoured is a per-firmware
 * question, which is why {@link tizenKeyClaimReport} exists - the probe prints
 * it rather than this module asserting a capability we have not seen.
 *
 * Nothing here throws. It runs before the first paint on hardware whose API
 * surface we are still learning, and a diagnostic that white-screens the TV has
 * cost more than the bug it was chasing.
 */

/**
 * Samsung TV remote key codes → the `key` value the app already handles.
 *
 * Sourced from Samsung's published TV key table and confirmed against the
 * `?probe=1` key echo on a QB24C. Codes the app has no meaning for are
 * deliberately absent: translating them would only invent events.
 */
export const TIZEN_KEY_CODES: Record<number, string> = {
  // Navigation. Arrows and Enter are standard W3C on this firmware already and
  // are NOT listed here - re-dispatching them would double every keypress.
  10009: "Escape", // RETURN / Back - the one that was never being delivered
  10182: "Escape", // EXIT - same intent, and see the guard below
  // Colour keys, which hospitality remotes use as shortcuts.
  403: "ColorF0Red",
  404: "ColorF1Green",
  405: "ColorF2Yellow",
  406: "ColorF3Blue",
  // Channel up/down, for the built-in guide's zapping (TV-4). Without these,
  // a Samsung remote can only zap with the arrow keys - the two buttons a
  // guest actually reaches for do nothing at all.
  427: "ChannelUp",
  428: "ChannelDown",
  // Transport, for signage/video surfaces.
  415: "MediaPlay",
  19: "MediaPause",
  413: "MediaStop",
  10252: "MediaPlayPause",
};

/**
 * The keys we ask the firmware to hand to us instead of acting on itself.
 *
 * `Exit` is the load-bearing one. The digits and colour keys are claimed by the
 * WGT bootloader too (tizen/index.html) - doing it again is harmless and makes
 * the app correct when it is NOT running inside our widget, e.g. opened in the
 * panel's own browser for a quick test.
 */
const CLAIM = [
  "Exit",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "ColorF0Red",
  "ColorF1Green",
  "ColorF2Yellow",
  "ColorF3Blue",
  "MediaPlayPause",
  // Claimed so the panel's own tuner does not act on them while Foyer owns the
  // foreground. Unclaimed, ChannelUp on a Samsung leaves our guide and changes
  // the firmware's channel behind us.
  "ChannelUp",
  "ChannelDown",
];

let claimReport = "not attempted";

/** What `registerKey` actually accepted, for the probe to print. */
export function tizenKeyClaimReport(): string {
  return claimReport;
}

interface TvInputDevice {
  registerKey?: (name: string) => void;
}

function inputDevice(): TvInputDevice | null {
  try {
    const t = (
      window as unknown as { tizen?: { tvinputdevice?: TvInputDevice } }
    ).tizen;
    const dev = t?.tvinputdevice;
    return dev && typeof dev.registerKey === "function" ? dev : null;
  } catch {
    return null;
  }
}

/**
 * Ask the firmware for every key in {@link CLAIM}. Records how many were
 * accepted AND names the ones that were not, because "13/16" on a photograph
 * cannot tell us whether the one that failed was `Exit` - which is the only one
 * that decides whether this ticket is closed.
 */
function claimKeys(): void {
  const dev = inputDevice();
  if (!dev) {
    claimReport = "tizen.tvinputdevice absent";
    return;
  }
  const refused: string[] = [];
  let got = 0;
  for (const name of CLAIM) {
    try {
      dev.registerKey!(name);
      got += 1;
    } catch {
      // Not every key name exists on every firmware. Keep going: a remote that
      // claims 15 of 16 is far better than one that aborts on the first miss.
      refused.push(name);
    }
  }
  claimReport =
    `${got}/${CLAIM.length}` +
    (refused.length ? ` · refused: ${refused.join(" ")}` : "");
}

/** True when this keypress would take the guest out of Foyer. */
function isExitKey(e: KeyboardEvent): boolean {
  return e.keyCode === 10182 || e.key === "XF86Exit";
}

/**
 * Translate one Samsung keypress into the event the app is written against.
 *
 * Runs in the CAPTURE phase so it lands before useFocusGrid, the launcher kit
 * and PairingScreen, all of which listen on `window` in the bubble phase. The
 * original event is stopped dead and a synthetic one is dispatched in its
 * place, so downstream handlers see exactly one event with a `key` they
 * understand - rather than both the raw code and the translation.
 */
function onKeyDownCapture(e: KeyboardEvent): void {
  try {
    // Already a name the app understands (a USB keyboard, the panel's own
    // arrows, a browser during development) - leave it completely alone.
    const mapped = TIZEN_KEY_CODES[e.keyCode];
    if (!mapped) return;
    if (e.key === mapped) return;

    // THE GUARD. `preventDefault` is what stops the firmware acting on Exit
    // itself once the key has been claimed; without the claim this is a no-op
    // and the app closes anyway, which is the measurement TIZEN-15 wants.
    e.preventDefault();
    e.stopImmediatePropagation();

    const synthetic = new KeyboardEvent("keydown", {
      key: mapped,
      code: mapped,
      bubbles: true,
      cancelable: true,
    });
    // Re-dispatch from the same element so focus-scoped handlers
    // (`el.contains(document.activeElement)`) still see the right target.
    (e.target instanceof Element ? e.target : window).dispatchEvent(synthetic);
  } catch {
    // A translation layer must never be the reason a TV stops responding to
    // its remote. Swallow and let the raw event through on the next press.
  }
}

/**
 * Block the firmware's default action for Exit even when nothing downstream
 * handled the synthetic event. Registered on `keyup` as well because some
 * Samsung firmwares act on the release, not the press.
 */
function onKeyUpCapture(e: KeyboardEvent): void {
  try {
    if (isExitKey(e)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  } catch {
    /* see above */
  }
}

let installed = false;

/**
 * Install the bridge. Idempotent, safe on every platform (on web and Android
 * the code map simply never matches), and safe to call before React mounts.
 */
export function installTizenKeyBridge(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  claimKeys();
  window.addEventListener("keydown", onKeyDownCapture, true);
  window.addEventListener("keyup", onKeyUpCapture, true);
}

/** Test seam: undo {@link installTizenKeyBridge}. */
export function uninstallTizenKeyBridge(): void {
  if (!installed) return;
  installed = false;
  claimReport = "not attempted";
  window.removeEventListener("keydown", onKeyDownCapture, true);
  window.removeEventListener("keyup", onKeyUpCapture, true);
}
